import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from '@mui/material';
import {
  Login,
  Logout,
  PersonAdd,
  Edit,
  Delete,
  Email,
  CreditCard,
  CloudUpload,
  Warning,
  Error,
  CheckCircle,
  Info,
  Refresh,
  FilterList,
  AccessTime,
  Business,
  Assignment,
} from '@mui/icons-material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api from '../../services/api';

interface Activity {
  id: string;
  timestamp: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  activity_type: string;
  activity_level: 'info' | 'success' | 'warning' | 'error';
  club_id?: string;
  club_name?: string;
  card_id?: string;
  title: string;
  description?: string;
  metadata?: any;
}

interface ActivityLogProps {
  userId?: string;
  clubId?: string;
  limit?: number;
  showFilters?: boolean;
  title?: string;
}

const ActivityLog: React.FC<ActivityLogProps> = ({
  userId,
  clubId,
  limit = 50,
  showFilters = true,
  title = 'Recent Activities',
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [filters, setFilters] = useState({
    activity_type: '',
    activity_level: '',
    days: 7,
  });

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (clubId) params.append('club_id', clubId);
      if (filters.activity_type) params.append('activity_type', filters.activity_type);
      if (filters.activity_level) params.append('activity_level', filters.activity_level);
      params.append('limit', limit.toString());

      const response = await api.get(`/activities?${params.toString()}`);
      setActivities(response.data);
    } catch (err: any) {
      setError('Failed to load activities');
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, clubId, filters, limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const getActivityIcon = (type: string) => {
    const iconMap: { [key: string]: React.ReactElement } = {
      user_login: <Login />,
      user_logout: <Logout />,
      user_created: <PersonAdd />,
      user_updated: <Edit />,
      user_deleted: <Delete />,
      
      club_created: <Business />,
      club_updated: <Business />,
      club_configured: <Business />,
      
      card_created: <CreditCard />,
      card_generated: <CreditCard />,
      card_email_sent: <Email />,
      
      template_uploaded: <CloudUpload />,
      template_updated: <Edit />,
      
      import_started: <Assignment />,
      import_completed: <CheckCircle />,
      import_failed: <Error />,
      
      email_sent: <Email />,
      email_failed: <Error />,
      
      system_error: <Error />,
      system_warning: <Warning />,
    };

    return iconMap[type] || <Info />;
  };

  const getLevelColor = (level: string) => {
    const colorMap: { [key: string]: 'info' | 'success' | 'warning' | 'error' } = {
      info: 'info',
      success: 'success',
      warning: 'warning',
      error: 'error',
    };

    return colorMap[level] || 'default';
  };

  const formatActivityTime = (timestamp: string) => {
    const date = parseISO(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM d, yyyy h:mm a');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime />
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showFilters && (
            <IconButton
              size="small"
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            >
              <FilterList />
            </IconButton>
          )}
          <IconButton size="small" onClick={fetchActivities}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {activities.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activities to display
          </Typography>
        </Box>
      ) : (
        <List sx={{ py: 0 }}>
          {activities.map((activity, index) => (
            <React.Fragment key={activity.id}>
              <ListItem
                sx={{
                  py: 1.5,
                  px: 0,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: `${getLevelColor(activity.activity_level)}.light`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: `${getLevelColor(activity.activity_level)}.main`,
                    }}
                  >
                    {getActivityIcon(activity.activity_type)}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {activity.title}
                      </Typography>
                      {activity.club_name && (
                        <Chip
                          label={activity.club_name}
                          size="small"
                          icon={<Business sx={{ fontSize: 14 }} />}
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      {activity.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {activity.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {activity.user_name && (
                          <Typography variant="caption" color="text.secondary">
                            by {activity.user_name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatActivityTime(activity.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondaryTypographyProps={{
                    component: 'div'
                  }}
                />
              </ListItem>
              {index < activities.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        PaperProps={{
          sx: { width: 280, p: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Filter Activities
        </Typography>
        
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Activity Type</InputLabel>
          <Select
            value={filters.activity_type}
            onChange={(e) => setFilters({ ...filters, activity_type: e.target.value })}
            label="Activity Type"
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="user_login">User Login</MenuItem>
            <MenuItem value="user_created">User Created</MenuItem>
            <MenuItem value="club_updated">Club Updated</MenuItem>
            <MenuItem value="card_generated">Card Generated</MenuItem>
            <MenuItem value="email_sent">Email Sent</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Level</InputLabel>
          <Select
            value={filters.activity_level}
            onChange={(e) => setFilters({ ...filters, activity_level: e.target.value })}
            label="Level"
          >
            <MenuItem value="">All Levels</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            setFilterAnchorEl(null);
            fetchActivities();
          }}
        >
          Apply Filters
        </Button>
      </Menu>
    </Paper>
  );
};

export default ActivityLog;